import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, Grid, Stack, Tooltip, IconButton } from "@mui/material";
import {
  Bell,
  Ticket,
  Trophy,
  CheckCheck,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";

const NotificationHero = ({
  totalCount = 0,
  unreadCount = 0,
  lotteryCount = 0,
  winnerCount = 0,
  onMarkAllAsRead,
  onRefresh,
  refreshing = false,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Gradient Accent Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={3.5} alignItems="center">
        {/* Left Section: Title, Description & Action Buttons */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)",
                }}
              >
                <Bell size={22} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: colors.slate[900],
                    letterSpacing: "-0.5px",
                    fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Notifications & Alerts
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Live On-Chain & Platform Activity Feed
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.925rem", sm: "1rem" },
                maxWidth: 580,
                lineHeight: 1.6,
              }}
            >
              Stay updated with your latest lottery draws, ticket purchases, winner payouts,
              gift card claims, and creator partner announcements in real time.
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {unreadCount > 0 && onMarkAllAsRead && (
                <CommonButton
                  variant="secondary"
                  size="small"
                  onClick={onMarkAllAsRead}
                  startIcon={<CheckCheck size={16} />}
                >
                  Mark All Read ({unreadCount})
                </CommonButton>
              )}

              <Tooltip title="Refresh notification feed">
                <IconButton
                  onClick={onRefresh}
                  disabled={refreshing}
                  sx={{
                    bgcolor: colors.slate[50],
                    color: colors.slate[700],
                    border: `1px solid ${colors.slate[200]}`,
                    borderRadius: borderRadiusPx.md,
                    p: 1,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: colors.slate[100],
                      color: colors.primary.main,
                      borderColor: colors.slate[300],
                    },
                  }}
                >
                  <RefreshCw
                    size={18}
                    style={{
                      animation: refreshing ? "spin 1s linear infinite" : "none",
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>

        {/* Right Section: Metric Stats Grid */}
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            {/* Stat 1: Total Alerts */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Bell size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Total Alerts
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.slate[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {totalCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                  All events recorded
                </Typography>
              </Box>
            </Grid>

            {/* Stat 2: Unread Alerts */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: unreadCount > 0 ? colors.blue[50] : colors.slate[50],
                  border: `1px solid ${unreadCount > 0 ? colors.blue[200] : colors.slate[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: unreadCount > 0 ? colors.primary.main : colors.slate[200],
                      color: unreadCount > 0 ? colors.neutral.white : colors.slate[600],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Sparkles size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: unreadCount > 0 ? colors.primary.dark : colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Unread
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: unreadCount > 0 ? colors.primary.dark : colors.slate[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {unreadCount}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: unreadCount > 0 ? colors.primary.main : colors.slate[500],
                    fontWeight: 600,
                  }}
                >
                  {unreadCount > 0 ? "Awaiting review" : "All caught up"}
                </Typography>
              </Box>
            </Grid>

            {/* Stat 3: Lottery Events */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.amber[100],
                      color: colors.amber[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Ticket size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.amber[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Lotteries & Draws
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.amber[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {lotteryCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.amber[700], fontWeight: 600 }}>
                  Rounds & rollovers
                </Typography>
              </Box>
            </Grid>

            {/* Stat 4: Winner Events */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.emerald[100],
                      color: colors.emerald[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Trophy size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.emerald[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Winners & Payouts
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.emerald[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {winnerCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.emerald[700], fontWeight: 600 }}>
                  Prize disbursements
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

NotificationHero.propTypes = {
  totalCount: PropTypes.number,
  unreadCount: PropTypes.number,
  lotteryCount: PropTypes.number,
  winnerCount: PropTypes.number,
  onMarkAllAsRead: PropTypes.func,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
};

export default NotificationHero;
